import { useEffect, useRef, useState, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

function getWsUrl(userId, displayName) {
  const base = API.replace(/^http/, 'ws');
  const params = new URLSearchParams();
  if (userId) params.set('user_id', userId);
  if (displayName) params.set('display_name', displayName);
  return `${base}/api/ws/chat?${params.toString()}`;
}

const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 15000];
const PING_INTERVAL = 25000;
const TYPING_THROTTLE_MS = 2000;
const TYPING_EXPIRE_MS = 3500;

export function useChat(userId, displayName, onAccessExpired) {
  const [messages, setMessages] = useState([]);
  const [connected, setConnected] = useState(false);
  const [onlineCount, setOnlineCount] = useState(0);
  const [typingUsers, setTypingUsers] = useState({});
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [lastModEvent, setLastModEvent] = useState(null);
  const wsRef = useRef(null);
  const retriesRef = useRef(0);
  const pingRef = useRef(null);
  const mountedRef = useRef(true);
  const lastTypingSentRef = useRef(0);
  const typingCleanupRef = useRef(null);
  const accessExpiredRef = useRef(false);

  // Periodically clean up stale typing indicators
  useEffect(() => {
    typingCleanupRef.current = setInterval(() => {
      setTypingUsers(prev => {
        const now = Date.now();
        const next = {};
        let changed = false;
        for (const [uid, info] of Object.entries(prev)) {
          if (now - info.timestamp < TYPING_EXPIRE_MS) {
            next[uid] = info;
          } else {
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, 1000);
    return () => { if (typingCleanupRef.current) clearInterval(typingCleanupRef.current); };
  }, []);

  const handleMessage = useCallback((msg) => {
    if (msg.type === 'init' && msg.messages) {
      setMessages(msg.messages);
      setPinnedMessages(msg.pinned_messages || []);
      if (typeof msg.online_count === 'number') setOnlineCount(msg.online_count);
    } else if (msg.type === 'new_message' && msg.data) {
      setMessages(prev => {
        if (prev.some(m => m.id === msg.data.id)) return prev;
        return [...prev, msg.data];
      });
      // Clear typing indicator for this sender
      if (msg.data.user_id) {
        setTypingUsers(prev => {
          if (!prev[msg.data.user_id]) return prev;
          const next = { ...prev };
          delete next[msg.data.user_id];
          return next;
        });
      }
    } else if (msg.type === 'online_count' && typeof msg.count === 'number') {
      setOnlineCount(msg.count);
    } else if (msg.type === 'typing' && msg.user_id && msg.user_id !== userId) {
      setTypingUsers(prev => ({
        ...prev,
        [msg.user_id]: { display_name: msg.display_name || 'Someone', timestamp: Date.now() },
      }));
    } else if (msg.type === 'message_deleted' && msg.message_id) {
      setMessages(prev => prev.map(m => m.id === msg.message_id ? { ...m, deleted: true } : m));
      setPinnedMessages(prev => prev.filter(m => m.id !== msg.message_id));
    } else if (msg.type === 'message_edited' && msg.message_id) {
      setMessages(prev => prev.map(m =>
        m.id === msg.message_id ? { ...m, content: msg.content, edited_at: msg.edited_at } : m
      ));
    } else if (msg.type === 'message_pinned' && msg.data) {
      setPinnedMessages(prev => {
        if (prev.some(m => m.id === msg.data.id)) return prev;
        return [...prev, msg.data];
      });
    } else if (msg.type === 'message_unpinned' && msg.message_id) {
      setPinnedMessages(prev => prev.filter(m => m.id !== msg.message_id));
    } else if (msg.type === 'user_moderated') {
      setLastModEvent({ ...msg, _ts: Date.now() });
    } else if (msg.type === 'access_expired') {
      accessExpiredRef.current = true;
      if (onAccessExpired) onAccessExpired();
    }
  }, [userId, onAccessExpired]);

  const cleanup = useCallback(() => {
    if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
  }, []);

  const connect = useCallback(() => {
    if (!mountedRef.current) return;
    try {
      const ws = new WebSocket(getWsUrl(userId, displayName));
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) return;
        setConnected(true);
        retriesRef.current = 0;
        pingRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current) return;
        if (event.data === 'pong') return;
        try {
          handleMessage(JSON.parse(event.data));
        } catch {}
      };

      ws.onclose = (event) => {
        if (!mountedRef.current) return;
        cleanup();
        setConnected(false);
        // Do NOT reconnect if access was explicitly denied/expired
        if (accessExpiredRef.current || event.code === 4003 || event.code === 4001) return;
        const delay = RECONNECT_DELAYS[Math.min(retriesRef.current, RECONNECT_DELAYS.length - 1)];
        retriesRef.current += 1;
        setTimeout(() => { if (mountedRef.current) connect(); }, delay);
      };

      ws.onerror = () => { ws.close(); };
    } catch {
      setConnected(false);
      const delay = RECONNECT_DELAYS[Math.min(retriesRef.current, RECONNECT_DELAYS.length - 1)];
      retriesRef.current += 1;
      setTimeout(() => { if (mountedRef.current) connect(); }, delay);
    }
  }, [userId, displayName, handleMessage, cleanup]);

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      cleanup();
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
    };
  }, [connect, cleanup]);

  const sendTyping = useCallback(() => {
    const now = Date.now();
    if (now - lastTypingSentRef.current < TYPING_THROTTLE_MS) return;
    lastTypingSentRef.current = now;
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'typing' }));
    }
  }, []);

  const fetchOlderMessages = useCallback(async () => {
    if (messages.length === 0) return false;
    const oldest = messages[0];
    try {
      const res = await fetch(`${API}/api/chat/messages?before=${encodeURIComponent(oldest.created_at)}&limit=50`, { credentials: 'same-origin' });
      if (!res.ok) return false;
      const data = await res.json();
      if (data.messages && data.messages.length > 0) {
        const older = data.messages.reverse();
        setMessages(prev => {
          const ids = new Set(prev.map(m => m.id));
          const unique = older.filter(m => !ids.has(m.id));
          return [...unique, ...prev];
        });
        return data.has_more;
      }
      return false;
    } catch {
      return false;
    }
  }, [messages]);

  return { messages, connected, onlineCount, typingUsers, sendTyping, fetchOlderMessages, pinnedMessages, lastModEvent };
}
